import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Text,
  Input,
  Card,
  Badge,
  Avatar,
  AvatarGroup,
  tokens,
} from '@fluentui/react-components';
import { Dismiss12Regular, Search24Regular, ArrowUndo24Regular } from '@fluentui/react-icons';
import { KanbanCard } from '../../types/kanban';
import { AvatarColor } from './avatarUtils';
import { getPriorityColor } from './types';

interface ArchivedTasksDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isReadOnly: boolean;
  archivedCards: KanbanCard[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  getFilteredArchivedCards: () => KanbanCard[];
  onUnarchiveCard: (cardId: string) => void;
  userPhotos: Record<string, string>;
  getAvatarColor: (userId: string) => AvatarColor;
  themedScrollbarClass: string;
}

export const ArchivedTasksDialog: React.FC<ArchivedTasksDialogProps> = ({
  isOpen,
  onClose,
  isReadOnly,
  archivedCards,
  searchQuery,
  setSearchQuery,
  getFilteredArchivedCards,
  onUnarchiveCard,
  userPhotos,
  getAvatarColor,
  themedScrollbarClass,
}) => {
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) {
          onClose();
        }
      }}
    >
      <DialogSurface style={{ maxWidth: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
        <Button
          appearance="subtle"
          icon={<Dismiss12Regular />}
          onClick={onClose}
          style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 1 }}
        />
        <DialogTitle>
          Archived Tasks
        </DialogTitle>
        <DialogBody>
          <DialogContent className={themedScrollbarClass} style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '12px' }}>
            {archivedCards.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Text size={400} style={{ color: tokens.colorNeutralForeground2 }}>
                  No archived tasks
                </Text>
              </div>
            ) : (
              <>
                {/* Search Bar */}
                <div style={{ marginBottom: '1rem' }}>
                  <Input
                    placeholder="Search archived tasks..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    contentBefore={<Search24Regular />}
                    style={{ width: '100%' }}
                  />
                </div>

                {/* Archived Tasks List */}
                {(() => {
                  const filteredCards = getFilteredArchivedCards();
                  if (filteredCards.length === 0) {
                    return (
                      <div style={{ padding: '2rem', textAlign: 'center' }}>
                        <Text size={400} style={{ color: tokens.colorNeutralForeground2 }}>
                          No archived tasks match your search
                        </Text>
                      </div>
                    );
                  }

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {filteredCards.map((card) => (
                        <Card
                          key={card.id}
                          style={{
                            border: `1px solid ${tokens.colorNeutralStroke1}`,
                            backgroundColor: tokens.colorNeutralBackground1,
                          }}
                        >
                          {card.color && card.color !== '' && (
                            <div
                              style={{
                                height: '8px',
                                backgroundColor: card.color,
                                borderTopLeftRadius: '8px',
                                borderTopRightRadius: '8px',
                                margin: '-16px -16px 8px -16px'
                              }}
                            />
                          )}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                <Badge
                                  appearance="filled"
                                  color={getPriorityColor(card.priority) as any}
                                  size="small"
                                >
                                  {card.priority.toUpperCase()}
                                </Badge>
                                <Text weight="semibold" size={400}>{card.title}</Text>
                              </div>
                              {card.description && (
                                <Text size={300} style={{ color: tokens.colorNeutralForeground2, display: 'block', marginBottom: '0.5rem' }}>
                                  {card.description}
                                </Text>
                              )}
                              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                {card.assignedTo && card.assignedTo.length > 0 && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <AvatarGroup layout="spread" size={24}>
                                      {card.assignedTo.slice(0, 3).map((user: any) => (
                                        <Avatar
                                          key={user.id}
                                          image={userPhotos[user.id] ? { src: userPhotos[user.id] } : undefined}
                                          name={user.displayName}
                                          color={getAvatarColor(user.id)}
                                          size={24}
                                        />
                                      ))}
                                    </AvatarGroup>
                                    {card.assignedTo.length > 3 && (
                                      <Text size={200}>+{card.assignedTo.length - 3}</Text>
                                    )}
                                  </div>
                                )}
                                {card.archivedAt && (
                                  <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                                    Archived {new Date(card.archivedAt).toLocaleDateString()}
                                  </Text>
                                )}
                              </div>
                            </div>
                            {!isReadOnly && (
                              <Button
                                icon={<ArrowUndo24Regular />}
                                appearance="subtle"
                                size="small"
                                onClick={() => onUnarchiveCard(card.id)}
                              >
                                Restore
                              </Button>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  );
                })()}
              </>
            )}
          </DialogContent>
        </DialogBody>
        <DialogActions style={{ paddingTop: '1rem', justifyContent: 'flex-end' }}>
          <Button appearance="primary" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
};
